import { DS } from '../../design-system'
import { StyleSheet, Switch, TextInput } from 'react-native'
import { Dropdown } from './Dropdown'
import { translate } from '../../i18n'
import { DateTimePickers } from './DateTimePickers'
import { ACTION_TIME_VALUES, BLIND_TIME_VALUES, STARTING_CHIPS_VALUES } from '@/constants'

export const MAX_NAME_LENGTH = 20

interface CreateScheduledTournamentFormProps {
  isNameEmpty?: boolean
  setIsNameEmpty?: (value: boolean) => void
  tournamentName: string
  tournamentBlindsLength: string
  tournamentStartingChips: string
  tournamentActionTime: string
  nameTooLong?: boolean
  setTournamentName: (value: string) => void
  setTournamentBlindsLength: (value: string) => void
  setTournamentStartingChips: (value: string) => void
  setTournamentActionTime: (value: string) => void
  scheduledDate: Date
  onScheduledDateChange: (date: Date) => void
  scheduleForLater: boolean
  onScheduleForLaterChange: (value: boolean) => void
  /** When true, form is editing an existing schedule; schedule-for-later switch is hidden */
  isEdit?: boolean
  /** When true (e.g. lounge flow), schedule-for-later switch is hidden and only "start now" applies */
  hideScheduleForLater?: boolean
}

const STARTING_CHIPS = 10000

/** "None" (blinds never increase) is stored as a blind timer of 0. */
const NO_BLIND_INCREASE = translate('createGameV2.none')
const BLIND_TIME_OPTIONS = [NO_BLIND_INCREASE, ...BLIND_TIME_VALUES]

export const CreateScheduledTournamentForm = ({
  isNameEmpty = false,
  setIsNameEmpty,
  nameTooLong = false,
  tournamentName,
  tournamentBlindsLength,
  tournamentStartingChips,
  tournamentActionTime,
  setTournamentName,
  setTournamentBlindsLength,
  setTournamentStartingChips,
  setTournamentActionTime,
  scheduledDate,
  onScheduledDateChange,
  scheduleForLater,
  onScheduleForLaterChange,
  isEdit = false,
  hideScheduleForLater = false
}: CreateScheduledTournamentFormProps) => {
  // Use saved/prop values as display defaults so edit mode shows existing schedule data
  const displayBlinds =
    tournamentBlindsLength && Number(tournamentBlindsLength) > 0 ? `${tournamentBlindsLength} min` : NO_BLIND_INCREASE
  const displayStartingChips = tournamentStartingChips || STARTING_CHIPS.toString()
  const displayActionTime = tournamentActionTime ? `${tournamentActionTime} sec` : ACTION_TIME_VALUES[1]

  const handleBlindsChange = (selectedItem: string) => {
    if (selectedItem === NO_BLIND_INCREASE) {
      setTournamentBlindsLength('0')
      return
    }
    const selectedValue = selectedItem.replace(' min', '')
    setTournamentBlindsLength(selectedValue)
  }

  const handleActionTimeChange = (selectedItem: string) => {
    const selectedValue = selectedItem.replace(' sec', '')
    setTournamentActionTime(selectedValue)
  }

  return (
    <DS.Frame style={styles.container}>
      <DS.Text variant="Heading 4/Regular">{translate('createTournamentScreen.formTitle')}</DS.Text>
      <DS.Frame gap="8">
        <DS.Text variant="Body Medium/Medium" color="Global/Black">
          {translate('createTournamentScreen.name')}
        </DS.Text>
        <TextInput
          style={[styles.input, isNameEmpty ? styles.nameIsEmpty : {}]}
          value={tournamentName}
          onChangeText={(value) => {
            setTournamentName(value)
            setIsNameEmpty?.(false)
          }}
        />
        {nameTooLong && (
          <DS.Text variant="Caption/Regular" color="Utility/Warning">
            {translate('createTournamentScreen.nameTooLong', { length: MAX_NAME_LENGTH })}
          </DS.Text>
        )}
      </DS.Frame>
      <DS.Frame gap="8">
        <DS.Text variant="Body Medium/Medium" color="Global/Black">
          {translate('createTournamentScreen.blindsLength')}
        </DS.Text>
        <Dropdown
          key={`blinds-${displayBlinds}`}
          data={BLIND_TIME_OPTIONS}
          defaultValue={displayBlinds}
          onSelect={(selectedItem) => handleBlindsChange(selectedItem)}
        />
      </DS.Frame>
      <DS.Frame gap="8">
        <DS.Text variant="Body Medium/Medium" color="Global/Black">
          {translate('createTournamentScreen.startingChips')}
        </DS.Text>
        <Dropdown
          key={`chips-${displayStartingChips}`}
          data={STARTING_CHIPS_VALUES}
          defaultValue={displayStartingChips}
          onSelect={(selectedItem) => setTournamentStartingChips(selectedItem)}
        />
      </DS.Frame>
      <DS.Frame gap="8">
        <DS.Text variant="Body Medium/Medium" color="Global/Black">
          {translate('createTournamentScreen.actionTime')}
        </DS.Text>
        <Dropdown
          key={`action-${displayActionTime}`}
          data={ACTION_TIME_VALUES}
          defaultValue={displayActionTime}
          onSelect={(selectedItem) => handleActionTimeChange(selectedItem)}
        />
      </DS.Frame>
      {!hideScheduleForLater && (
        <DateTimePickers value={scheduledDate} onChange={onScheduledDateChange} isDisabled={!scheduleForLater} />
      )}
      {!isEdit && !hideScheduleForLater && (
        <DS.Frame style={styles.switchContainer}>
          <DS.Text variant="Heading 4/Regular" color="Global/Black">
            {translate('createTournamentScreen.schedule')}
          </DS.Text>
          <Switch
            onValueChange={onScheduleForLaterChange}
            value={scheduleForLater}
            trackColor={{ false: DS.palette['Brand/Light Grey'], true: DS.palette['Brand/Maroon'] }}
            thumbColor={DS.palette['Global/White']}
          />
        </DS.Frame>
      )}
    </DS.Frame>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: DS.palette['Global/White'],
    padding: 24,
    gap: 24,
    borderRadius: 16
  },
  input: {
    height: 48,
    borderRadius: 5,
    paddingStart: 10,
    marginTop: 6,
    borderColor: DS.palette['Global/Black'],
    borderWidth: 1
  },
  nameIsEmpty: {
    borderColor: DS.palette['Utility/Warning']
  },
  dropdown: {
    height: 48,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 5,
    paddingStart: 10,
    marginTop: 6,
    borderColor: DS.palette['Global/Black'],
    borderWidth: 1,
    width: '100%'
  },
  dropdownPickerImage: {
    width: 16,
    height: 16,
    resizeMode: 'contain',
    tintColor: DS.palette['Global/Black']
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  }
})
